import { HttpEventType } from '@angular/common/http';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import readXlsxFile from 'read-excel-file';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { PriceListAsImportModel } from 'src/app/models/imports/price-list-as-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PriceListDetailService } from 'src/app/services/archive/price-list-detail.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-import-price-list-as',
  templateUrl: './import-price-list-as.component.html',
  styleUrls: ['./import-price-list-as.component.scss'],
  standalone: false
})
export class ImportPriceListAsComponent implements OnInit {

  private readonly branchService = inject(BranchService);
  private readonly companyService = inject(CompanyService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly priceListDetailService = inject(PriceListDetailService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);
  private readonly windowService = inject(WindowService);

  branchCode: number = 0;
  branchId: number = 0;
  companyCode: number = 0;
  companyId: number = 0;
  eventFile: any;
  errorPriceList: number = 0;
  filters: ColumnResponseModel[] = [];
  formImport: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  listCompanies: any[] = [];
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  nameFile: string = 'Ningún archivo seleccionado.';
  nameWindow: string = 'Importación Listas de Precios';
  permissionUpdatable: boolean = false;
  priceListAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>;
  projectName = ProjectName.projectName;
  subscription: Subscription;
  url: string = "price-list-import";
  visibleTxt: boolean = false;

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    this.headerService.setTitle(this.nameWindow);
    this.getPermission();
    this.validateForm();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  attach() {
    document.getElementById('fileImport')?.click();
  }

  clearInputFile() {
    this.nameFile = 'Ningún archivo seleccionado.';
    if (this.eventFile) {
      this.eventFile.target.value = '';
    }
  }

  downloadErrorsTxt() {
    if (!this.priceListAlerts || this.priceListAlerts.length === 0) {
      this.toastHandlerService.showToast(
        'error',
        'No hay alertas para exportar'
      );
      return;
    }

    this.priceListDetailService.exportImportErrors(this.priceListAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresImportacionMovimientosAlquiler_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast(
          'error',
          'Error al exportar el archivo'
        );
      }
    });
  }

  downloadGuideImport() {
    this.priceListDetailService.downloadGuideImportPriceList().subscribe(data => {
      switch (data.type) {
        case HttpEventType.Response: {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = "GuiaListasDePrecioAS.docx";
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
          break;
        }
      }
    });
  }

  getNameFile(file: any) {
    this.eventFile = file;
    const files = this.eventFile.target.files;
    if (files.length != 0) {
      for (let i = 0; i < files.length; i++) {
        this.nameFile = files[i].name;
      }
    } else {
      this.nameFile = 'Ningún archivo seleccionado.';
    }
  }

  async getAllLists(...listsToLoad: Array<'companies' | 'branches'>) {
    try {
      const lists = {
        companies: async () => {
          this.listCompanies = await firstValueFrom(
            this.companyService.getAllCompaniesForSelectAsync()
          );
        },
        branches: async () => {
          this.listBranches = await firstValueFrom(
            this.branchService.GetAllBranchesByCompanyIdForSelectAsync(this.companyId)
          );
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.companies()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getBranch(branchId: number | null) {
    if (!branchId) {
      this.branchId = 0;
      this.branchCode = 0;
      return;
    }

    const branch = this.listBranches.find(x => x.value == branchId);
    this.branchId = branchId;
    this.branchCode = branch.code;
  }

  async getCompany(companyId: number | null) {
    if (!companyId) {
      this.companyId = 0;
      this.companyCode = 0;
      this.listBranches = [];
      this.validateForm();
      return;
    }

    const company = this.listCompanies.find(x => x.value == companyId);
    this.companyId = companyId;
    this.companyCode = company.code;
    await this.getAllLists('branches');
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists('companies'),
      this.getPermissionProfileByUrl()
    ])
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al obtener los permisos de la ventana.', error);
    }
  }

  importPriceList() {
    this.priceListAlerts = [];
    this.errorPriceList = 0;
    this.visibleTxt = false;
    Object.keys(this.formImport.controls).forEach(key => {
      const control = this.formImport.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formImport.valid) {
      let isOk: boolean = true;
      const input = document.getElementById("fileImport") as any;
      const rowsImport = new Array<PriceListAsImportModel>();
      let row: PriceListAsImportModel;
      let counter: number = 1, priceListCode: number, priceListAlterCode: string, priceListName: string, priceListDiscount: number, priceListObservation: string,
        articleCode: number, baseValue: number, clientValue: number, discountValue: number, swManual: boolean;

      if (!input.files[0]) {
        this.toastHandlerService.showToast(
          'error',
          'No ha seleccionado ningún archivo.'
        );
        return;
      }

      readXlsxFile(input.files[0]).then(async (rows) => {
        if (rows.length <= 1) {
          this.toastHandlerService.showToast(
            'warning',
            'El archivo ingresado no tiene registros para importar.'
          );
          return;
        }

        rows.splice(0, 1);
        rows.forEach(element => {
          counter = counter + 1;
          let rowHasError = false;

          //A. Código Compañía:
          const companyCode = parseFloat(element[0] as string);
          if (isNaN(companyCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna A. El código de la Compañía no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          } else {
            if (companyCode != this.companyCode) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna A. La Compañía del archivo no es igual a la Compañía seleccionada.`;
              this.priceListAlerts.push(alert);
              rowHasError = true;
            }
          }

          //B. Código Sucursal:
          const branchCode = parseFloat(element[1] as string);
          if (isNaN(branchCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna B. El código de la Sucursal no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          } else {
            if (branchCode != this.branchCode) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna B. La Sucursal del archivo no es igual a la Sucursal seleccionada.`;
              this.priceListAlerts.push(alert);
              rowHasError = true;
            }
          }

          //C. Codigo Lista:
          priceListCode = parseFloat(element[2] as string);
          if (isNaN(priceListCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna C. El código de la Lista no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          } else if (priceListCode <= 0) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}. El código de la Lista debe ser mayor a 0.`;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //D. Codigo Alterno:
          priceListAlterCode = element[3] as string;
          if (priceListAlterCode != null && priceListAlterCode.length > 10) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna D. La observación de la lista de precios supera el tamaño máximo de caracteres (10). `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //E. Nombre Lista:
          priceListName = element[4] as string;
          if (priceListName == null || priceListName == "") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna E. El nombre de la Lista no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          } else if (priceListName.length > 70) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna E. El nombre de la Lista de precios supera el tamaño máximo de caracteres (70).`;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //F. Descuento Lista:
          priceListDiscount = parseFloat(element[5] as string);
          if (isNaN(priceListDiscount)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna F. El descuento de la Lista no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //G. Observación:
          priceListObservation = element[6] as string;
          if (priceListObservation != null && priceListObservation != "" && priceListObservation.length > 250) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}Columna G. La observación de la Lista de precios supera el tamaño máximo de caracteres (250).`;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //H. Código Artículo:
          articleCode = parseFloat(element[7] as string);
          if (isNaN(articleCode)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna H. El código del Artículo no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //I. Valor Base:
          baseValue = parseFloat(element[8] as string);
          if (isNaN(baseValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna I. El valor base no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //J. Valor Cliente:
          clientValue = parseFloat(element[9] as string);
          if (isNaN(clientValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna J. El valor cliente no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //K. Descuento Lista:
          discountValue = parseFloat(element[10] as string);
          if (isNaN(discountValue)) {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna K. El descuento no es válido. `;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }

          //L. Manual:
          if (element[11] as string != "S" && element[11] as string != "N") {
            const alert = new PreInvoicingAlertModel();
            alert.alertType = 1;
            alert.message = `Fila ${counter}, Columna L. El campo Manual debe ser valores como: S / N.`;
            this.priceListAlerts.push(alert);
            rowHasError = true;
          }
          swManual = element[11] as string == "S";

          if (!rowHasError) {
            row = new PriceListAsImportModel(counter, this.companyId, this.branchId, priceListCode,
              priceListAlterCode, priceListName, priceListDiscount, priceListObservation,
              articleCode, baseValue, clientValue, discountValue, swManual);
            rowsImport.push(row);
          } else {
            isOk = false;
          }
        });

        if (!isOk || rowsImport.length === 0) {
          this.errorPriceList = 1;
          this.visibleTxt = true;
          this.clearInputFile();
          this.nameFile = 'Ningún archivo seleccionado.';
          this.toastHandlerService.showToast(
            'error',
            'La importación terminó con errores, puede exportarlos si lo desea',
            'error'
          );
          return;
        }

        await this.processImportPriceList(rowsImport);
      });
    }
  }

  async processImportPriceList(rowsImport: Array<PriceListAsImportModel>) {
    try {
      const response = await firstValueFrom(this.priceListDetailService.importPriceListAs(rowsImport));
      this.loadPreInvoicingModel = response;
      this.priceListAlerts = response?.alerts ?? [];
      this.errorPriceList = response?.preInvoicingStateId;

      if (this.errorPriceList === 1) {
        this.visibleTxt = true;
        this.clearInputFile();
        this.nameFile = 'Ningún archivo seleccionado.';
        this.toastHandlerService.showToast(
          'error',
          'La importación terminó con errores, puede exportarlos si lo desea.',
          'ERROR'
        );
      } else if (this.priceListAlerts.length > 0) {
        this.visibleTxt = true;
        this.toastHandlerService.showToast(
          'error',
          'La importación terminó con alertas, puede exportarlas si lo desea'
        );
      } else {
        this.visibleTxt = false;
        this.toastHandlerService.showToast(
          'success',
          'La importación terminó exitosamente.'
        );
      }
      this.clearInputFile();
    } catch (error) {
      this.nameFile = 'Ningún archivo seleccionado.';
      console.error('Error al importar los movimientos.', error);
    }
    (<HTMLInputElement>document.getElementById('fileImport')).value = '';
  }

  validateForm() {
    this.formImport = this.formBuilder.group({
      companyId: [0, [Validators.required, Validators.min(1)]],
      branchId: [0, [Validators.required, Validators.min(1)]],
    });
  }
}